import { AlertTriangle } from "lucide-react";
import { useTranslations } from "use-intl";
import { Badge } from "@/components/ui/badge";
import type { ClosingFxInfo } from "@/clients/journalEntryApiClient";
import { formatAmount } from "@financialharmony/ui";

interface ClosingEntryFxSummaryProps {
  closingFx: ClosingFxInfo;
  /** The closing entry's own journal lines — scanned for a 7960/3960 FX gain/loss line. */
  items: Array<{ account_number: string; debit: number; credit: number }>;
}

function formatSek(amount: number | null): string | null {
  if (amount == null) return null;
  return formatAmount(amount);
}

/**
 * FIN-1023 D2/D3/AC-6 (screens.md T2) — the closing-entry drawer's own header line, extending
 * `SekEquivalent`'s "≈ X SEK @ rate (date)" convention with the "Booked ... · Settled ... · FX
 * gain/loss ..." shape a closing entry specifically needs (two different amounts — what was
 * booked at upload time vs. what was actually settled — not one estimate). A warning badge shows
 * whenever R did not come from a real SEK-account bank debit (AC-6: "a bank leg booked from a
 * rate instead of a real SEK figure says so") — both the foreign-account settlement-day-rate case
 * and the external/manual user-entered case are "a rate," not an observed fact.
 */
export default function ClosingEntryFxSummary({ closingFx, items }: ClosingEntryFxSummaryProps) {
  const t = useTranslations("consultantReceivables.fx");
  const { bookedSekAmount, bookedExchangeRate, bookedExchangeRateDate, settledSekAmount, fxSettlementSource } = closingFx;
  const bookedSek = formatSek(bookedSekAmount);
  const settledSek = formatSek(settledSekAmount);
  if (bookedSek == null && settledSek == null) return null;

  const lossLine = items.find(i => i.account_number === "7960" && i.debit > 0);
  const gainLine = items.find(i => i.account_number === "3960" && i.credit > 0);
  const isFallback = fxSettlementSource != null && fxSettlementSource !== "real_sek_debit";

  return (
    <p className="text-xs text-muted-foreground flex items-center gap-1.5 flex-wrap">
      {bookedSek != null && (
        <span>
          {t("booked", { amount: bookedSek })}
          {bookedExchangeRate != null && bookedExchangeRateDate && (
            t("bookedRate", { rate: bookedExchangeRate.toFixed(4), date: bookedExchangeRateDate })
          )}
        </span>
      )}
      {bookedSek != null && settledSek != null && <span>·</span>}
      {settledSek != null && (
        <span>
          {t("settled", { amount: settledSek })}
          {fxSettlementSource && t("settledSource", { source: t(`sources.${fxSettlementSource}`) })}
        </span>
      )}
      {lossLine && (
        <>
          <span>·</span>
          <span>{t("loss", { amount: formatSek(lossLine.debit) })}</span>
        </>
      )}
      {gainLine && (
        <>
          <span>·</span>
          <span>{t("gain", { amount: formatSek(gainLine.credit) })}</span>
        </>
      )}
      {isFallback && (
        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 gap-1">
          <AlertTriangle className="h-3 w-3" aria-hidden="true" />
          {t("estimated")}
        </Badge>
      )}
    </p>
  );
}
